import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import logger from './utils/logger';

// Define notification types
export type NotificationType = 'success' | 'error' | 'info';

// Define notification state interface
interface NotificationState {
  visible: boolean;
  message: string;
  type: NotificationType;
  counter: number;
}

// Define keyframes for the loading spinner
const spinKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Types
type RegistrationStatus = 'none' | 'pending' | 'success' | 'already_registered' | 'error';

// Constants
const MESSAGES = {
  ALREADY_REGISTERED: (email: string) => `${email} is already registered. You'll continue receiving updates.`,
  REGISTRATION_SUCCESS: (email: string) => `SUCCESS: ${email} has been registered!`,
  PROCESSING: (email: string) => `PROCESSING: Registering ${email}...`,
  OFFLINE: (email: string) => `OFFLINE: Cannot reach our servers. Your registration will be retried when you're back online.`,
  SERVER_DOWN: (email: string) => `SERVER UNAVAILABLE: Our registration system is currently unavailable. Please try again later.`,
  EMPTY_EMAIL: 'Please enter an email address to register.',
  SERVER_ERROR: (email: string) => `Unable to register ${email}. Please try again later.`,
};

// Counter to manage multiple notifications
let notificationCounter = 0;

// Notification Component
const Notification: React.FC<{
  message: string;
  type: NotificationType;
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  const colors = {
    success: '#4caf50',
    error: '#f44336',
    info: '#2196f3'
  };

  // Add local visibility state for animation control
  const [isVisible, setIsVisible] = useState(false);
  
  // Add ref to the notification element
  const notificationRef = useRef<HTMLDivElement>(null);

  // Use useEffect to animate in and log when the notification is rendered
  useEffect(() => {
    logger.log('Notification component mounting with message:', message);
    
    // Force a reflow before setting visible to true
    const timer = setTimeout(() => {
      setIsVisible(true);
      logger.log('Notification made visible');
    }, 10);
    
    // Return cleanup function
    return () => {
      logger.log('Notification unmounted:', message);
      clearTimeout(timer);
    };
  }, [message]);
  
  // Ensure click events on the notification are stopped from propagating
  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={notificationRef}
      onClick={handleContainerClick}
      style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: isVisible 
          ? 'translateX(-50%) translateY(0)' 
          : 'translateX(-50%) translateY(-20px)',
        backgroundColor: colors[type],
        color: 'white',
        padding: '16px 24px',
        borderRadius: '4px',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.6)',
        zIndex: 999999,
        fontWeight: 'bold',
        fontSize: '18px',
        maxWidth: '90%',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: 'auto',
        willChange: 'transform, opacity',
      }}
      data-testid="notification"
    >
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          logger.log('Close button clicked for notification:', message);
          onClose();
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'white',
          marginLeft: '16px',
          fontSize: '20px',
          cursor: 'pointer',
          fontWeight: 'bold',
          padding: '0 8px',
        }}
        data-testid="notification-close-button"
      >
        ✕
      </button>
    </div>
  );
};

// Registration Status Indicator Component
const RegistrationStatusIndicator: React.FC<{
  status: RegistrationStatus;
  onClose: () => void;
}> = ({ status, onClose }) => {
  // Return null for all statuses, including 'error'
  return null;
};

// Spinner Component
const LoadingSpinner: React.FC = () => (
  <div
    style={{
      width: '16px',
      height: '16px',
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px',
      display: 'inline-block',
    }}
  />
);

// Main EmailForm Component
const EmailForm: React.FC = () => {
  // State
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus>('none');
  const [notification, setNotification] = useState<NotificationState>({
    visible: false,
    message: '',
    type: 'info',
    counter: 0
  });
  
  // Refs
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Theme and responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Define keyframes for fade-in animation
  const fadeInKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px) translateX(-50%); }
    to { opacity: 1; transform: translateY(0) translateX(-50%); }
  }
  `;
  
  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Show notification function
  const showNotification = (message: string, type: NotificationType = 'info') => {
    logger.log('showNotification called:', message, type);
    notificationCounter += 1;
    
    // Clear existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
    
    // Set notification state
    setNotification({
      visible: true,
      message,
      type,
      counter: notificationCounter
    });
    
    // Auto-hide after 5 seconds for success/info notifications
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        logger.log('Auto-hiding notification:', message);
        hideNotification();
      }, 5000);
    }
  };
  
  // Hide notification function
  const hideNotification = () => {
    setNotification(prev => ({
      ...prev,
      visible: false
    }));
  };
  
  // Helper function to check if server is reachable
  const checkServerConnection = async (): Promise<boolean> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/health`, { 
        method: 'HEAD',
        cache: 'no-store'
      });
      return response.ok;
    } catch (e) {
      logger.error('Server health check failed:', e);
      return false;
    }
  };
  
  // Reset form function
  const resetForm = () => {
    // Only reset form if registration was successful or already registered
    if (registrationStatus === 'success' || registrationStatus === 'already_registered') {
      setEmail('');
    }
    
    // Reset submission state
    setTimeout(() => {
      setIsSubmitting(false);
      setIsEmailValid(true);
    }, 100);
  };
  
  // Handle email save function
  const handleSaveEmail = async () => {
    logger.log('handleSaveEmail called');
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    try {
      const currentEmail = email.trim();
      
      // Skip if email is empty
      if (!currentEmail) {
        showNotification(MESSAGES.EMPTY_EMAIL, 'error');
        return;
      }
      
      // Validate email format before submission
      if (!validateEmail(currentEmail)) {
        showNotification('Please enter a valid email address', 'error');
        return;
      }
      
      // Set submitting state
      setIsSubmitting(true);
      setRegistrationStatus('pending');
      
      // Show processing notification
      logger.log('Showing processing notification for:', currentEmail);
      showNotification(MESSAGES.PROCESSING(currentEmail), 'info');
      
      // Make actual API call to the server
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: currentEmail }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Server error:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      logger.log('Server response:', data);
      
      if (data.status === 'success') {
        setRegistrationStatus('success');
        showNotification(MESSAGES.REGISTRATION_SUCCESS(currentEmail), 'success');
      } else if (data.status === 'already_registered') {
        setRegistrationStatus('already_registered');
        showNotification(MESSAGES.ALREADY_REGISTERED(currentEmail), 'info');
      } else {
        throw new Error('Unexpected server response');
      }
      
      resetForm();
    } catch (error) {
      logger.error('Error during submission:', error);
      showNotification('Registration is currently unavailable. Please try again later.', 'info');
      setRegistrationStatus('error');
      setIsSubmitting(false);
    }
  };
  
  // Process pending registrations when back online
  const processOfflineRegistrations = async () => {
    try {
      const pendingRegistrationsJson = localStorage.getItem('pendingRegistrations');
      if (!pendingRegistrationsJson) return;
      
      const pendingRegistrations = JSON.parse(pendingRegistrationsJson);
      if (!pendingRegistrations.length) return;
      
      showNotification('Back online! Attempting to register your pending email(s)...', 'info');
      
      let successCount = 0;
      let failCount = 0;
      let alreadyRegisteredCount = 0;
      const stillPending: string[] = [];
      
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      for (const email of pendingRegistrations) {
        try {
          const response = await fetch(`${apiUrl}/api/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success') {
              successCount++;
            } else if (data.status === 'already_registered') {
              alreadyRegisteredCount++;
            }
          } else {
            failCount++;
            stillPending.push(email);
          }
        } catch (error) {
          logger.error(`Failed to register pending email ${email}:`, error);
          failCount++;
          stillPending.push(email);
        }
      }
      
      // Update localStorage
      if (stillPending.length > 0) {
        localStorage.setItem('pendingRegistrations', JSON.stringify(stillPending));
      } else {
        localStorage.removeItem('pendingRegistrations');
      }
      
      // Show summary notification
      let message = '';
      if (successCount > 0) {
        message += `Successfully registered ${successCount} email(s). `;
      }
      if (alreadyRegisteredCount > 0) {
        message += `${alreadyRegisteredCount} email(s) were already registered. `;
      }
      if (failCount > 0) {
        message += `Failed to register ${failCount} email(s) - will retry later.`;
      }
      
      showNotification(message.trim(), failCount > 0 ? 'error' : 'success');
    } catch (error) {
      logger.error('Error processing offline registrations:', error);
    }
  };
  
  // Effect to validate email
  useEffect(() => {
    setIsEmailValid(validateEmail(email));
  }, [email]);
  
  // Effect to reset form
  useEffect(() => {
    if (!isSubmitting && !email) {
      setIsEmailValid(true);
    }
  }, [isSubmitting, email]);
  
  // Effect to add mobile meta tag
  useEffect(() => {
    const existingAppleTag = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (existingAppleTag && !document.querySelector('meta[name="mobile-web-app-capable"]')) {
      const newMetaTag = document.createElement('meta');
      newMetaTag.setAttribute('name', 'mobile-web-app-capable');
      newMetaTag.setAttribute('content', 'yes');
      document.head.appendChild(newMetaTag);
    }
  }, []);
  
  // Effect to clean up notification timeouts
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);
  
  // Effect to handle network reconnection
  useEffect(() => {
    const handleOnline = () => processOfflineRegistrations();
    
    window.addEventListener('online', handleOnline);
    
    if (navigator.onLine) {
      processOfflineRegistrations();
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  
  // Form submission handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    logger.log('Save Email button clicked');
    handleSaveEmail();
  };
  
  const handleClose = () => {
    logger.log('Close notification clicked');
    hideNotification();
  };
  
  return (
    <>
      {/* Animation styles */}
      <style>{spinKeyframes}</style>
      <style>{fadeInKeyframes}</style>
      
      {/* Notification Container - always present in DOM with a high z-index */}
      <div 
        data-testid="notification-container"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          pointerEvents: 'none',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          overflow: 'visible'
        }}
      >
        {notification.visible && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={handleClose}
          />
        )}
      </div>
      
      {/* Registration Status Indicator */}
      <RegistrationStatusIndicator 
        status={registrationStatus} 
        onClose={() => setRegistrationStatus('none')} 
      />
      
      {/* Email Form */}
      <Box 
        component="form"
        onSubmit={handleSubmit}
        sx={{ 
          marginTop: isMobile ? 1.5 : 2,
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: isMobile ? 1.5 : 2,
          position: 'relative',
          zIndex: 10, // Add z-index to ensure form is above other elements
        }}
        data-testid="email-form"
      >
        <TextField
          id="email-input"
          name="email"
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (!isEmailValid) {
              setIsEmailValid(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSaveEmail();
            }
          }}
          fullWidth
          margin="none"
          size={isMobile ? "small" : "medium"}
          error={email.length > 0 && !isEmailValid}
          helperText={email.length > 0 && !isEmailValid 
            ? "Please enter a valid email address" 
            : "Register to receive updates. You can only register once."}
          sx={{ 
            flex: 1,
            '& .MuiInputBase-root': {
              borderRadius: isMobile ? '8px' : '4px',
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              fontSize: '0.75rem',
            }
          }}
          inputProps={{ 'data-testid': 'email-input' }}
        />
        
        <div style={{ 
          width: isMobile ? '100%' : 'auto',
          marginTop: isMobile && email.length > 0 && !isEmailValid ? '20px' : undefined,
        }}>
          <button 
            id="save-email-btn"
            ref={saveButtonRef}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              logger.log('Save Email button clicked');
              // Add a visual flash effect to confirm button was clicked
              if (saveButtonRef.current) {
                saveButtonRef.current.style.backgroundColor = '#0d5a94';
                setTimeout(() => {
                  if (saveButtonRef.current) {
                    saveButtonRef.current.style.backgroundColor = '';
                  }
                }, 150);
              }
              
              // Break call stack to prevent React batching issues
              setTimeout(() => {
                handleSaveEmail();
              }, 10);
            }}
            disabled={isSubmitting} 
            style={{
              height: isMobile ? '40px' : '56px',
              width: '100%',
              minWidth: isMobile ? '100%' : '120px',
              borderRadius: '8px',
              fontWeight: 500,
              border: 'none',
              background: isSubmitting
                ? '#cccccc' 
                : 'linear-gradient(90deg, #1976d2, #2ec4b6)',
              color: 'white',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'all 0.3s ease',
            }}
            data-testid="save-email-button"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner />
                <span>Processing...</span>
              </>
            ) : (
              'Save Email'
            )}
          </button>
        </div>
      </Box>
    </>
  );
};

export default EmailForm;
